import DeleteAccountButton from '../../components/DeleteAccountButton';
import { splitForSpeech } from '../../utils/speechChunking';
import PoweredByFHO from '../../components/PoweredByFHO';
import PARTNER_THEMES, { getPartnerTheme, getPilotTheme } from '../../config/partnerThemes';
import PilotWelcome from '../../components/PilotWelcome';
import React, { useState, useEffect, useRef } from 'react';
import ConversationActions from '../../components/ConversationActions';
import PaywallModal from '../../components/auth/PaywallModal';
import MoodCheckInModal from '../../components/MoodCheckInModal';
import FeedbackButton from '../../components/FeedbackButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../services/api_config';
import ResponseCounter from '../../components/auth/ResponseCounter';

// ============================================================================
// SAFE EARTH-TONE THEME - Calming, Grounding, Safe
// ============================================================================
const SAFE_THEME = {
  pageBg: '#2A2520',
  pageBgGradient: 'linear-gradient(135deg, #2A2520 0%, #231F1B 100%)',
  cardBg: '#3D3630',
  sidebarBg: '#352F29',
  inputBg: 'rgba(184, 169, 154, 0.08)',
  primary: '#7C9A82',
  primaryLight: '#9BB5A0',
  primaryDark: '#6B8B72',
  primaryGradient: 'linear-gradient(135deg, #526F57, #47624D)',
  secondary: '#B8A99A',
  secondaryLight: '#D4C5B5',
  accent: '#C4A77D',
  accentLight: '#D4B78D',
  userBubble: 'linear-gradient(135deg, #526F57, #47624D)',
  assistantBubble: 'rgba(184, 169, 154, 0.12)',
  textPrimary: '#F5F2ED',
  textSecondary: 'rgba(245, 242, 237, 0.7)',
  textMuted: 'rgba(245, 242, 237, 0.5)',
  border: 'rgba(184, 169, 154, 0.2)',
  borderLight: 'rgba(184, 169, 154, 0.1)',
  listening: 'linear-gradient(135deg, #C4A77D, #D4B78D)',
  danger: '#dc3545',
  success: '#7C9A82',
};


// ---- Color schemes (pilot) ----
// Olive is the theme above. A pilot participant can pick Navy or Air Force from the
// menu; until they do, the colors follow the branch given at setup (the server decides).
// Colors only: no seals or emblems.
const SAFE_SCHEMES = {
  olive: {
    ...SAFE_THEME,
    headerBg: 'rgba(61, 54, 48, 0.5)',
    hoverBg: 'rgba(184, 169, 154, 0.18)',
    glow: 'rgba(124, 154, 130, 0.3)',
    glowStrong: 'rgba(124, 154, 130, 0.5)',
    onPrimary: '#1f1b18',
  },
  navy: {
    ...SAFE_THEME,
    pageBg: '#141C2B',
    pageBgGradient: 'linear-gradient(135deg, #141C2B 0%, #101726 100%)',
    cardBg: '#1F2A3D',
    sidebarBg: '#1A2435',
    inputBg: 'rgba(143, 168, 200, 0.08)',
    primary: '#8FA8C8',
    primaryLight: '#A9BDD6',
    primaryDark: '#7B96BA',
    primaryGradient: 'linear-gradient(135deg, #3E5A82, #4F6E9B)',
    secondary: '#B4BECC',
    secondaryLight: '#D3DAE4',
    accent: '#D4B15A',
    accentLight: '#E0C377',
    userBubble: 'linear-gradient(135deg, #3E5A82, #34507A)',
    assistantBubble: 'rgba(143, 168, 200, 0.12)',
    textPrimary: '#F2F4F7',
    textSecondary: 'rgba(242, 244, 247, 0.7)',
    textMuted: 'rgba(242, 244, 247, 0.5)',
    border: 'rgba(143, 168, 200, 0.2)',
    borderLight: 'rgba(143, 168, 200, 0.1)',
    listening: 'linear-gradient(135deg, #D4B15A, #E0C377)',
    success: '#8FA8C8',
    headerBg: 'rgba(31, 42, 61, 0.5)',
    hoverBg: 'rgba(143, 168, 200, 0.18)',
    glow: 'rgba(62, 90, 130, 0.35)',
    glowStrong: 'rgba(62, 90, 130, 0.55)',
    onPrimary: '#101A2B',
  },
  airforce: {
    ...SAFE_THEME,
    pageBg: '#1F2730',
    pageBgGradient: 'linear-gradient(135deg, #1F2730 0%, #1A2129 100%)',
    cardBg: '#2C3742',
    sidebarBg: '#26303A',
    inputBg: 'rgba(160, 180, 200, 0.08)',
    primary: '#7DB1E0',
    primaryLight: '#9CC4E8',
    primaryDark: '#6A9FD0',
    primaryGradient: 'linear-gradient(135deg, #2F6496, #3A72A6)',
    secondary: '#B7C1CB',
    secondaryLight: '#D5DCE3',
    accent: '#C9D1DA',
    accentLight: '#DDE3E9',
    userBubble: 'linear-gradient(135deg, #346A9E, #2C5C8B)',
    assistantBubble: 'rgba(160, 180, 200, 0.12)',
    textPrimary: '#F3F5F7',
    textSecondary: 'rgba(243, 245, 247, 0.7)',
    textMuted: 'rgba(243, 245, 247, 0.5)',
    border: 'rgba(160, 180, 200, 0.2)',
    borderLight: 'rgba(160, 180, 200, 0.1)',
    listening: 'linear-gradient(135deg, #C9D1DA, #DDE3E9)',
    success: '#7DB1E0',
    headerBg: 'rgba(44, 55, 66, 0.5)',
    hoverBg: 'rgba(160, 180, 200, 0.18)',
    glow: 'rgba(52, 106, 158, 0.35)',
    glowStrong: 'rgba(52, 106, 158, 0.55)',
    onPrimary: '#10202E',
  },
  // Standard SAFE: Sage (neutral, the look SAFE has always had), Slate and Rose.
  sage: {
    ...SAFE_THEME,
    headerBg: 'rgba(61, 54, 48, 0.5)',
    hoverBg: 'rgba(184, 169, 154, 0.18)',
    glow: 'rgba(124, 154, 130, 0.3)',
    glowStrong: 'rgba(124, 154, 130, 0.5)',
    onPrimary: '#1f1b18',
  },
  slate: {
    ...SAFE_THEME,
    pageBg: '#1E2226',
    pageBgGradient: 'linear-gradient(135deg, #1E2226 0%, #1A1D21 100%)',
    cardBg: '#2B3035',
    sidebarBg: '#252A2F',
    inputBg: 'rgba(150, 165, 180, 0.08)',
    primary: '#8FA3B5',
    primaryLight: '#A9BAC9',
    primaryDark: '#7A90A4',
    primaryGradient: 'linear-gradient(135deg, #3F5466, #364A5B)',
    secondary: '#B5BDC4',
    secondaryLight: '#D2D8DD',
    accent: '#C08A5B',
    accentLight: '#D19E72',
    userBubble: 'linear-gradient(135deg, #3F5466, #364A5B)',
    assistantBubble: 'rgba(150, 165, 180, 0.12)',
    textPrimary: '#F2F4F5',
    textSecondary: 'rgba(242, 244, 245, 0.7)',
    textMuted: 'rgba(242, 244, 245, 0.5)',
    border: 'rgba(150, 165, 180, 0.2)',
    borderLight: 'rgba(150, 165, 180, 0.1)',
    listening: 'linear-gradient(135deg, #C08A5B, #D19E72)',
    success: '#8FA3B5',
    headerBg: 'rgba(43, 48, 53, 0.5)',
    hoverBg: 'rgba(150, 165, 180, 0.18)',
    glow: 'rgba(63, 84, 102, 0.35)',
    glowStrong: 'rgba(63, 84, 102, 0.55)',
    onPrimary: '#14191E',
  },
  rose: {
    ...SAFE_THEME,
    pageBg: '#2A1F24',
    pageBgGradient: 'linear-gradient(135deg, #2A1F24 0%, #241A1F 100%)',
    cardBg: '#3A2B32',
    sidebarBg: '#33262C',
    inputBg: 'rgba(212, 160, 176, 0.08)',
    primary: '#D4A0B0',
    primaryLight: '#E2B8C5',
    primaryDark: '#C58A9C',
    primaryGradient: 'linear-gradient(135deg, #7A4A5C, #6B3F50)',
    secondary: '#CDB5BE',
    secondaryLight: '#E3D2D8',
    accent: '#D9B77E',
    accentLight: '#E5C894',
    userBubble: 'linear-gradient(135deg, #7A4A5C, #6B3F50)',
    assistantBubble: 'rgba(212, 160, 176, 0.12)',
    textPrimary: '#F8F1F3',
    textSecondary: 'rgba(248, 241, 243, 0.7)',
    textMuted: 'rgba(248, 241, 243, 0.5)',
    border: 'rgba(212, 160, 176, 0.2)',
    borderLight: 'rgba(212, 160, 176, 0.1)',
    listening: 'linear-gradient(135deg, #D9B77E, #E5C894)',
    success: '#D4A0B0',
    headerBg: 'rgba(58, 43, 50, 0.5)',
    hoverBg: 'rgba(212, 160, 176, 0.18)',
    glow: 'rgba(122, 74, 92, 0.35)',
    glowStrong: 'rgba(122, 74, 92, 0.55)',
    onPrimary: '#22161B',
  },
};
// A pilot offers its own three; standard SAFE offers Sage, Slate and Rose.
const safeSchemeOrder = () => {
  const t = getPilotTheme();
  return (t && t.schemes) || (t ? ['olive', 'navy', 'airforce'] : ['sage', 'slate', 'rose']);
};
const SAFE_SCHEME_LABELS = { olive: 'Olive', navy: 'Navy', airforce: 'Air Force', sage: 'Sage', slate: 'Slate', rose: 'Rose' };
const safeSchemeKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return 'safeColorScheme_' + (u.id || u.email || 'anonymous');
  } catch (e) { return 'safeColorScheme_anonymous'; }
};
const readSafeScheme = () => {
  const allowed = safeSchemeOrder();
  let id = null;
  try { id = localStorage.getItem(safeSchemeKey()); } catch (e) { /* storage blocked */ }
  return allowed.includes(id) ? id : allowed[0];
};
const storeSafeScheme = (id) => { try { localStorage.setItem(safeSchemeKey(), id); } catch (e) { /* storage blocked */ } };
// The screens read SAFE_THEME as they draw, so a scheme is applied by copying it in.
const applySafeScheme = (id) => {
  const k = SAFE_SCHEMES[id] ? id : 'olive';
  Object.assign(SAFE_THEME, SAFE_SCHEMES[k]);
  return k;
};
applySafeScheme('olive');

// Standard SAFE uses the same welcome card, FHO-5 check-in and suggested practice as a
// pilot, under its own context so its readings never count toward a pilot's.
// Standard SAFE and stand-alone SAFE share one definition, in config/partnerThemes.js.
const SAFE_STANDARD = PARTNER_THEMES.safe;
const safeContext = () => (getPilotTheme() || SAFE_STANDARD);


// ---- Pilot voice (ElevenLabs, through our server) ----
// A reply is cut into pieces of a few sentences; the next piece is fetched while one plays.
// Any failure hands the rest of the reply to the browser voice, so speech never just stops.
const SILENT_WAV = 'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';
const pilotVoice = { run: 0, audio: null, unlocked: false, off: false, choice: null };
const pilotAudio = () => {
  if (!pilotVoice.audio && typeof Audio !== 'undefined') {
    pilotVoice.audio = new Audio();
    pilotVoice.audio.setAttribute('playsinline', '');
    pilotVoice.audio.preload = 'auto';
  }
  return pilotVoice.audio;
};
// Phones only let a page play sound it started from a tap; the first tap unlocks the player.
if (typeof document !== 'undefined') {
  const unlock = () => {
    const a = pilotAudio();
    if (!a || pilotVoice.unlocked || pilotVoice.unlocking) return;
    if (a.src && !a.paused) return;
    pilotVoice.unlocking = true;
    a.src = SILENT_WAV;
    const done = (ok) => { pilotVoice.unlocking = false; pilotVoice.unlocked = ok; };
    let p;
    try { p = a.play(); } catch (e) { done(false); return; }
    if (p && p.then) p.then(() => done(true), () => done(false)); else done(true);
  };
  ['touchend', 'click', 'keydown', 'pointerup'].forEach((ev) => document.addEventListener(ev, unlock, true));
}
const voiceText = (t) => String(t || '')
  .replace(/MSTAG[\s\S]*?ENDTAG/g, ' ')
  .replace(/^#{1,6}\s+(.+?)\s*$/gm, '$1.')
  .replace(/^\s*-{3,}\s*$/gm, ' ')
  .replace(/^\s*[-*\u2022]\s+/gm, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/FHO-OS/gi, 'foe OS').replace(/\bFHO\b/gi, 'foe')
  .replace(/\s+/g, ' ').trim();
const voiceChunks = (t) => {
  const out = [];
  let cur = '';
  for (const p of splitForSpeech(t)) {
    if (cur && cur.length + 1 + p.length > 420) { out.push(cur); cur = p; } else { cur = cur ? cur + ' ' + p : p; }
  }
  if (cur) out.push(cur);
  return out;
};
const fetchVoice = (text) => {
  let token = null;
  try { token = localStorage.getItem('token'); } catch (e) { /* storage blocked */ }
  return fetch('/api/v1/safe/voice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ text }),
  }).then((r) => {
    if (r.status === 503 || r.status === 403) pilotVoice.off = true;   // not set up, or not a pilot account
    if (!r.ok) throw new Error('voice ' + r.status);
    return r.blob();
  }).then((b) => URL.createObjectURL(b));
};

// The device voice uses only voices that run on the device itself. Some browsers also list
// voices that send the text to Google or Microsoft to be spoken (localService false); those are skipped.
const pickDeviceVoice = (voices) => {
  const local = (voices || []).filter((v) => v.localService && /^en([-_]|$)/i.test(v.lang || ''));
  const rank = (v) => {
    const n = v.name || '';
    if (/Samantha|Ava|Allison|Susan|Karen|Moira|Tessa|Serena/.test(n)) return 0;
    if (/Natural|Neural|Enhanced|Premium/i.test(n)) return 1;
    if (/Zira|Jenny|Aria/.test(n)) return 2;
    return 3;
  };
  return local.slice().sort((a, b) => rank(a) - rank(b))[0] || null;
};
const VOICE_SAMPLE_TEXT = "Hello. I'm here to listen whenever you're ready, so take your time.";
const previewVoice = (id) => {
  stopPilotVoice();
  try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ }
  if (id === 'device') {
    try {
      const u = new window.SpeechSynthesisUtterance(VOICE_SAMPLE_TEXT);
      u.rate = 0.9; u.pitch = 0.85;
      const vs = window.speechSynthesis.getVoices();
      const pv = pickDeviceVoice(vs);
      if (pv) u.voice = pv;
      window.speechSynthesis.speak(u);
    } catch (e) { /* no speech */ }
    return;
  }
  const mine = ++pilotVoice.run;
  let token = null;
  try { token = localStorage.getItem('token'); } catch (e) { /* storage blocked */ }
  fetch('/api/v1/safe/voice-sample/' + encodeURIComponent(id), { headers: { Authorization: 'Bearer ' + token } })
    .then((r) => { if (!r.ok) throw new Error('sample ' + r.status); return r.blob(); })
    .then((b) => {
      if (mine !== pilotVoice.run) return;
      const a = pilotAudio();
      const url = URL.createObjectURL(b);
      a.onended = () => URL.revokeObjectURL(url);
      a.onerror = () => URL.revokeObjectURL(url);
      a.src = url;
      a.play().catch(() => {});
    })
    .catch((e) => console.warn('[FHO voice] preview failed:', e && e.message));
};
const stopPilotVoice = () => {
  pilotVoice.run++;
  const a = pilotVoice.audio;
  if (a) { try { a.pause(); } catch (e) { /* ignore */ } }
};
const playPilotVoice = (text, onStart, onEnd, onFail) => {
  const mine = ++pilotVoice.run;
  const parts = voiceChunks(voiceText(text));
  const a = pilotAudio();
  if (!a || !parts.length) { onEnd(); return; }
  let i = 0;
  let pending = fetchVoice(parts[0]);
  const step = () => {
    if (mine !== pilotVoice.run) return;
    if (i >= parts.length) { onEnd(); return; }
    const n = i++;
    const here = pending;
    pending = i < parts.length ? fetchVoice(parts[i]) : null;
    if (pending) pending.catch(() => {});
    here.then((url) => {
      if (mine !== pilotVoice.run) { URL.revokeObjectURL(url); return null; }
      a.onended = () => { URL.revokeObjectURL(url); step(); };
      a.onerror = () => { URL.revokeObjectURL(url); step(); };
      a.src = url;
      return a.play().then(() => { if (n === 0) onStart(); });
    }).catch((err) => {
      if (mine !== pilotVoice.run) return;
      console.warn('[FHO voice] using the browser voice for the rest of this reply:', err && err.message);
      pilotVoice.run++;
      onFail(parts.slice(n).join(' '));
    });
  };
  step();
};

const SAFEChat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  // Pilot color scheme: this browser's last choice first, then what the account holds.
  const [colorScheme, setColorScheme] = useState(() => applySafeScheme(readSafeScheme()));
  useEffect(() => {
    API.get('/api/v1/safe/color-scheme')
      .then((r) => {
        const id = r.data && r.data.active;
        if (id && safeSchemeOrder().includes(id)) { storeSafeScheme(id); setColorScheme(applySafeScheme(id)); }
      })
      .catch(() => {});
  }, []);
  const [voiceList, setVoiceList] = useState([]);
  const [voiceChoice, setVoiceChoice] = useState(null);
  useEffect(() => {
    API.get('/api/v1/safe/voices')
      .then((r) => {
        if (!r.data || !Array.isArray(r.data.voices)) return;
        setVoiceList(r.data.voices);
        setVoiceChoice(r.data.choice);
        pilotVoice.choice = r.data.choice;
      })
      .catch(() => {});
  }, []);
  const chooseVoice = (id) => {
    stopPilotVoice();
    try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ }
    pilotVoice.choice = id;
    if (id !== 'device') pilotVoice.off = false;
    setVoiceChoice(id);
    API.post('/api/v1/safe/voice-choice', { choice: id }).catch(() => {});
  };
  const chooseScheme = (id) => {
    storeSafeScheme(id);
    setColorScheme(applySafeScheme(id));
    API.post('/api/v1/safe/color-scheme', { scheme: id }).catch(() => {});
  };
  const [showPaywall, setShowPaywall] = useState(false);
  const [messages, setMessages] = useState([]);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [safeConversations, setSafeConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showMoodCheckIn, setShowMoodCheckIn] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || user.email || 'anonymous';
    const lastMoodDate = localStorage.getItem(`safeMoodCheckInDate_${userId}`);
    const today = new Date().toDateString();
    return lastMoodDate !== today;
  });
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('safeSessionId') || null);
  const [preMoodRecorded, setPreMoodRecorded] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || user.email || 'anonymous';
    const lastMoodDate = localStorage.getItem(`safeMoodCheckInDate_${userId}`);
    const today = new Date().toDateString();
    return lastMoodDate === today;
  });

  useEffect(() => {
    if (profile) {
      const pendingPrompt = localStorage.getItem('safe_pendingPrompt');
      if (pendingPrompt) {
        console.log('📬 Found pending prompt for SAFE:', pendingPrompt);
        localStorage.removeItem('safe_pendingPrompt');
        setInput(pendingPrompt);
        setTimeout(() => { sendMessage(pendingPrompt); }, 1000);
      }
    }
  }, [profile]);

  const [showPostMoodCheckIn, setShowPostMoodCheckIn] = useState(false);
  const [postMoodShown, setPostMoodShown] = useState(false);

  // Pilot: on a visit when the FHO-5 is due, the mood popup is held back so
  // the person sees one check-in, not two. Held until the server answers;
  // if the answer is 'not due' or the request fails, the popup behaves as usual.
  const [moodHeld, setMoodHeld] = useState(true);
  useEffect(() => {
    const pilot = safeContext();
    fetch('/api/v1/journey/assessment/pilot-status?context=' + encodeURIComponent(pilot.id),
      { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setMoodHeld(!!(d && d.due)); })
      .catch(() => { setMoodHeld(false); });
  }, []);

  // ============ 30 Mental Health Topics ============
  const mentalHealthTopics = [
    "Managing Daily Anxiety", "Calming Racing Thoughts", "Dealing with Panic Attacks",
    "Work-Related Stress", "Overthinking & Rumination", "Feeling Down or Hopeless",
    "Lack of Motivation", "Getting Through Hard Days", "Building Self-Esteem",
    "Overcoming Self-Doubt", "Finding My Purpose", "Setting Healthy Boundaries",
    "Grief and Loss", "Breaking Negative Patterns", "Building Healthy Routines",
    "Climbing Out of the 3D Swamp", "Removing the Hood of the Victim",
    "Transforming Despair into Happiness", "Using Autocebos for Healing",
    "Breaking Free from Dependency", "Activating My Flow MindStream",
    "Moving from Discord to Oneness", "Escaping the So-Soville Mindset",
    "Strengthening My I-ME Relationship", "Cultivating Positive Feelings for Flow",
    "Building Your I-THEE Connection", "Choosing Freedom Over Fear",
    "The 12 Life Choices for Mental Wellness", "Power Through Your Beliefs",
    "Clarity Through I-WE Relationships"
  ];

  const handleMoodSubmit = async (mood) => {
    const today = new Date().toDateString();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem(`safeMoodCheckInDate_${user.id || user.email || 'anonymous'}`, today);
    setPreMoodRecorded(true);
    setShowMoodCheckIn(false);
    try {
      const newSessionId = sessionId || `safe_${Date.now()}`;
      setSessionId(newSessionId);
      localStorage.setItem('safeSessionId', newSessionId);
      await API.post('/api/v1/mood/checkin', { session_id: newSessionId, mood: mood, is_pre_session: true });
    } catch (error) {
      if (error.message?.includes('403') || error.response?.status === 403) { setShowPaywall(true); return; }
      console.error('Error recording mood:', error);
    }
  };

  const handleMoodSkip = () => {
    const userSkip = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem(`safeMoodCheckInDate_${userSkip.id || userSkip.email || 'anonymous'}`, new Date().toDateString());
    setShowMoodCheckIn(false);
    if (!preMoodRecorded) setPreMoodRecorded(true);
  };

  const handlePostMoodSubmit = async (mood) => {
    setShowPostMoodCheckIn(false);
    setPostMoodShown(true);
    try {
      const currentSessionId = sessionId || localStorage.getItem('safeSessionId');
      await API.post('/api/v1/mood/checkin', {
        session_id: currentSessionId || `safe_${Date.now()}`,
        mood: mood,
        is_pre_session: false
      });
    } catch (error) {
      if (error.message?.includes('403') || error.response?.status === 403) { setShowPaywall(true); return; }
      console.error('Error recording post-session mood:', error);
    }
  };

  const handlePostMoodSkip = () => { setShowPostMoodCheckIn(false); setPostMoodShown(true); };

  useEffect(() => {
    if (exchangeCount >= 5 && preMoodRecorded && !postMoodShown && !showPostMoodCheckIn) {
      const timer = setTimeout(() => { setShowPostMoodCheckIn(true); }, 2000);
      return () => clearTimeout(timer);
    }
  }, [exchangeCount, preMoodRecorded, postMoodShown, showPostMoodCheckIn]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [responsesRemaining, setResponsesRemaining] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '';

  const useVoiceChat = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(() => {
      const saved = localStorage.getItem('safeVoiceEnabled');
      return saved ? JSON.parse(saved) : false;
    });
    const [isSpeaking, setIsSpeaking] = useState(false);
    const recognitionRef = useRef(null);
    const synthRef = useRef(null);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        synthRef.current = window.speechSynthesis;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
          recognitionRef.current = new SpeechRecognition();
          recognitionRef.current.continuous = false;
          recognitionRef.current.interimResults = true;
          recognitionRef.current.onresult = (event) => {
            let finalTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
              if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
            }
            if (finalTranscript) setTranscript(finalTranscript);
          };
          recognitionRef.current.onend = () => setIsListening(false);
          recognitionRef.current.onerror = () => setIsListening(false);
        }
      }
    }, []);
    useEffect(() => () => stopPilotVoice(), []);

    const startListening = () => {
      if (recognitionRef.current) { setTranscript(''); recognitionRef.current.start(); setIsListening(true); }
    };
    const stopListening = () => {
      if (recognitionRef.current) { recognitionRef.current.stop(); setIsListening(false); }
    };
    const stripMarkdownForVoice = (text) => text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1').replace(/__([^_]+)__/g, '$1').replace(/_([^_]+)_/g, '$1')
      .replace(/FHO-OS/gi, 'foe OS').replace(/FHO/gi, 'foe');
    const speak = (text) => {
      if (!voiceEnabled) return;
      if (!pilotVoice.off && pilotVoice.choice && pilotVoice.choice !== 'device') {
        if (synthRef.current) synthRef.current.cancel();
        playPilotVoice(text, () => setIsSpeaking(true), () => setIsSpeaking(false), (rest) => speakBrowser(rest));
        return;
      }
      speakBrowser(text);
    };
    const speakBrowser = (text) => {
      stopPilotVoice();
      if (!synthRef.current || !voiceEnabled) return;
      synthRef.current.cancel();
      const cleanText = stripMarkdownForVoice(text);
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = 0.9; utterance.pitch = 0.85;
      const voices = synthRef.current.getVoices();
      const preferredVoice = pickDeviceVoice(voices);
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      synthRef.current.speak(utterance);
    };
    const toggleVoice = () => {
      const newValue = !voiceEnabled;
      setVoiceEnabled(newValue);
      localStorage.setItem('safeVoiceEnabled', JSON.stringify(newValue));
      if (!newValue) { stopPilotVoice(); if (synthRef.current) synthRef.current.cancel(); }
    };
    return { isListening, transcript, voiceEnabled, isSpeaking, startListening, stopListening, speak, toggleVoice, setVoiceEnabled };
  };

  const voice = useVoiceChat();

  useEffect(() => { loadProfile(); }, []);
  useEffect(() => { if (voice.transcript) setInput(voice.transcript); }, [voice.transcript]);

  // Auto-send when browser silence detection cuts mic (continuous = false + onend fires)
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && input.trim()) {
      setTimeout(() => sendMessage(), 150);
    }
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promptParam = urlParams.get('prompt');
    const autoSend = urlParams.get('send') === 'true';
    if (promptParam && profile) {
      const decodedPrompt = decodeURIComponent(promptParam);
      setInput(decodedPrompt);
      window.history.replaceState({}, '', window.location.pathname);
      if (autoSend) setTimeout(() => { sendMessage(decodedPrompt); }, 1000);
    }
  }, [profile]);

  useEffect(() => {
    const journeyPrompt = localStorage.getItem('journeyFocusPrompt');
    if (journeyPrompt && profile) {
      localStorage.removeItem('journeyFocusPrompt'); localStorage.removeItem('journeyFocusCategory');
      localStorage.removeItem('guidedSessionId'); localStorage.removeItem('guidedSessionDimension');
      localStorage.removeItem('guidedSessionFocus'); localStorage.removeItem('guidedSessionFollowUps');
      setInput(journeyPrompt);
      setTimeout(() => { sendMessage(journeyPrompt); }, 800);
    }
  }, [profile]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadProfile = async () => {
    try {
      const response = await API.get('/api/v1/safe/profile');
      if (response.data) setProfile(response.data);
      else navigate('/safe/setup');
    } catch (err) { navigate('/safe/setup'); }
  };

  const loadSAFEHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await API.get("/api/v1/safe/conversations");
      setSafeConversations(res.data.conversations || []);
    } catch (error) { console.error("Error loading history:", error); }
    finally { setLoadingHistory(false); }
  };

  const loadConversationMessages = async (convId) => {
    try {
      const res = await API.get(`/api/v1/safe/conversations/${convId}`);
      const loadedMessages = res.data.messages.map(msg => ({ role: msg.role, content: msg.content }));
      setMessages(loadedMessages); setConversationId(convId); setShowConversationHistory(false);
    } catch (error) { console.error("Error loading conversation:", error); }
  };

  const sendMessage = async (directMessage = null) => {
    const messageToSend = directMessage || input.trim();
    if (!messageToSend || isLoading) return;
    const userMessage = { role: 'user', content: messageToSend };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/v1/safe/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ message: messageToSend, conversation_id: conversationId, partner: getPartnerTheme().id })
      });
      if (response.status === 403) { setShowPaywall(true); setMessages(prev => prev.slice(0, -1)); setIsLoading(false); return; }
      if (!response.ok) throw new Error('Failed to get response');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              // The backend mints the conversation ID on the first message and
              // returns it in every frame. Capture it so the rest of this
              // conversation carries the same ID, and a new one starts fresh.
              if (data.conversation_id && data.conversation_id !== conversationId) {
                setConversationId(data.conversation_id);
              }
              if (data.responses_remaining !== undefined) {
                setResponsesRemaining(data.responses_remaining);
                if (data.responses_remaining === 0) setTimeout(() => setShowPaywall(true), 2000);
              }
              if (data.chunk) {
                accumulatedText += data.chunk;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: accumulatedText, isStreaming: !data.done };
                  return updated;
                });
              }
            } catch (e) {}
          }
        }
      }
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0) updated[updated.length - 1] = { ...updated[updated.length - 1], isStreaming: false };
        return updated;
      });
      setExchangeCount(prev => prev + 1);
      if (voice.voiceEnabled && accumulatedText) voice.speak(accumulatedText);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally { setIsLoading(false); }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const renderMessageContent = (text) => {
    if (!text) return text;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      const linkText = match[1];
      const linkUrl = match[2];
      // SAFE shows no links: the words stay, the address is dropped.
      void linkUrl;
      parts.push(linkText);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', background: SAFE_THEME.pageBgGradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SAFE_THEME.textPrimary }}>
        <MoodCheckInModal palette={SAFE_THEME} isOpen={showMoodCheckIn && !moodHeld} onSubmit={handleMoodSubmit} onSkip={handleMoodSkip} isPostSession={false} />
        <MoodCheckInModal palette={SAFE_THEME} isOpen={showPostMoodCheckIn} onSubmit={handlePostMoodSubmit} onSkip={handlePostMoodSkip} isPostSession={true} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '24px', height: '24px', border: `3px solid ${SAFE_THEME.primary}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span>Loading your safe space...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {showMoodCheckIn && !preMoodRecorded && !moodHeld && (
        <MoodCheckInModal palette={SAFE_THEME} isOpen={true} onSubmit={handleMoodSubmit} onSkip={handleMoodSkip} isPostSession={false} />
      )}
      {showPostMoodCheckIn && (
        <MoodCheckInModal palette={SAFE_THEME} isOpen={true} onSubmit={handlePostMoodSubmit} onSkip={handlePostMoodSkip} isPostSession={true} />
      )}

      <div style={{ height: (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh')) ? '100dvh' : '100vh', overflow: 'hidden', background: SAFE_THEME.pageBgGradient, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="safe-hdr" style={{ padding: '10px 20px', borderBottom: `1px solid ${SAFE_THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: SAFE_THEME.headerBg }}>
          <style>{`
            @media (max-width: 600px) {
              .safe-hdr { flex-wrap: wrap !important; padding: 10px 12px 8px !important; row-gap: 4px; }
              .safe-hdr-l { flex: 0 0 44px !important; min-width: 44px !important; order: 1; }
              .safe-hdr-c { flex: 1 1 0 !important; min-width: 0 !important; order: 2; }
              .safe-hdr-c h1 { overflow-wrap: anywhere; }
              .safe-hdr-r { display: contents !important; }
              .safe-hdr-r > button { order: 3; flex: 0 0 44px; min-height: 44px; }
              .safe-hdr-pw { order: 4; flex: 0 0 100%; display: flex; justify-content: center; }
            }
          `}</style>
          <div className="safe-hdr-l" style={{ flex: 1, minWidth: '40px' }}></div>
          <div className="safe-hdr-c" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>🌿</span>
              <h1 style={{ color: SAFE_THEME.textPrimary, fontSize: getPilotTheme() ? '28px' : '18px', margin: 0, fontWeight: '600' }}>{getPilotTheme() ? getPilotTheme().partnerName : 'SAFE Buddy'}</h1>
            </div>
            <p style={{ color: getPilotTheme() ? SAFE_THEME.textPrimary : SAFE_THEME.textMuted, fontSize: getPilotTheme() ? '15px' : '12px', letterSpacing: getPilotTheme() ? '0.05em' : 'normal', margin: '4px 0 0 0' }}>
              {profile.military_status !== 'none' ? '🎖️ ' : ''}{getPilotTheme() ? getPilotTheme().partnerSub : 'Your Safe Space for Growth'}
            </p>
          </div>
          <div className="safe-hdr-r" style={{ flex: 1, justifyContent: 'flex-end', display: 'flex', alignItems: 'center', alignSelf: 'flex-start', paddingTop: '4px', gap: '12px' }}>
            <span className="safe-hdr-pw"><PoweredByFHO /></span>
            <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', color: SAFE_THEME.textPrimary, fontSize: '24px', cursor: 'pointer' }}>☰</button>
          </div>
        </div>

        {getPilotTheme() && getPilotTheme().crisisLine && (
          <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.35)', borderBottom: `1px solid ${SAFE_THEME.border}`, color: SAFE_THEME.textPrimary, fontSize: '16px', fontWeight: 600, textAlign: 'center', lineHeight: 1.5 }}>
            {getPilotTheme().crisisLine}
          </div>
        )}

        {responsesRemaining !== null && (
          <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 1000 }}>
            <ResponseCounter responsesRemaining={responsesRemaining} />
          </div>
        )}

        {/* Sidebar */}
        {sidebarOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }}
            onClick={() => setSidebarOpen(false)}>
            <div style={{ width: '280px', height: '100%', background: SAFE_THEME.sidebarBg, padding: '16px', marginLeft: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.3)', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: SAFE_THEME.textPrimary, fontSize: '24px', cursor: 'pointer' }}>×</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>🌿</span>
                  <h2 style={{ color: SAFE_THEME.textPrimary, margin: 0, fontSize: '18px' }}>SAFE</h2>
                </div>
              </div>

              {/* ── Reduced padding on all buttons so Logout is always visible ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button onClick={() => navigate('/safe/setup')}
                  style={{ padding: '8px 12px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textPrimary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✏️</span> Edit Profile
                </button>

                {(
                  <div style={{ padding: '10px 8px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px' }}>
                    <div style={{ color: SAFE_THEME.textPrimary, fontSize: '14px', margin: '0 4px 8px' }}>Colors</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {safeSchemeOrder().map((id) => {
                        const sc = SAFE_SCHEMES[id];
                        const on = colorScheme === id;
                        return (
                          <button key={id} type="button" onClick={() => chooseScheme(id)} aria-pressed={on}
                            style={{ flex: 1, minWidth: 0, padding: '8px 2px', background: sc.pageBg, border: `2px solid ${on ? SAFE_THEME.accent : SAFE_THEME.border}`, borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '16px' }}>
                              <span style={{ display: 'flex', width: '34px', height: '14px', borderRadius: '7px', overflow: 'hidden' }}>
                                <span style={{ flex: 1, background: sc.primary }} />
                                <span style={{ flex: 1, background: sc.accent }} />
                              </span>
                              {on && <span style={{ color: sc.textPrimary, fontSize: '14px', lineHeight: 1 }}>{'\u2713'}</span>}
                            </span>
                            <span style={{ color: sc.textPrimary, fontSize: '14px', lineHeight: 1.2, fontWeight: on ? 700 : 400 }}>{SAFE_SCHEME_LABELS[id]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {voiceList.length > 1 && (
                  <div style={{ padding: '10px 8px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px' }}>
                    <div style={{ color: SAFE_THEME.textPrimary, fontSize: '14px', margin: '0 4px 8px' }}>Voice</div>
                    <div role="radiogroup" aria-label="Voice" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {voiceList.map((v) => {
                        const on = voiceChoice === v.id;
                        return (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'stretch', gap: '6px' }}>
                            <button type="button" role="radio" aria-checked={on} onClick={() => chooseVoice(v.id)}
                              style={{ flex: 1, minWidth: 0, padding: '8px 10px', background: on ? SAFE_THEME.hoverBg : 'transparent', border: `2px solid ${on ? SAFE_THEME.accent : SAFE_THEME.border}`, borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                              <span style={{ display: 'block', color: SAFE_THEME.textPrimary, fontSize: '14px', fontWeight: on ? 700 : 500, lineHeight: 1.3 }}>{on ? '\u2713 ' : ''}{v.label}</span>
                              {v.note && <span style={{ display: 'block', color: SAFE_THEME.textSecondary, fontSize: '13px', lineHeight: 1.3, marginTop: '2px' }}>{v.note}</span>}
                            </button>
                            <button type="button" onClick={() => previewVoice(v.id)} aria-label={'Hear ' + v.label} title={'Hear ' + v.label}
                              style={{ width: '44px', flex: '0 0 44px', background: 'transparent', border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textPrimary, fontSize: '16px', cursor: 'pointer' }}>
                              {'\u25B6'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button onClick={() => { setSidebarOpen(false); setShowTopicsModal(true); }}
                  style={{ padding: '8px 12px', background: SAFE_THEME.primaryGradient, border: 'none', borderRadius: '8px', color: SAFE_THEME.textPrimary, textAlign: 'left', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💡</span> Explore 30 Topics
                </button>

                <button onClick={() => { setSidebarOpen(false); setMessages([]); setConversationId(null); }}
                  style={{ padding: '8px 12px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textPrimary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔄</span> New Conversation
                </button>

                <button onClick={() => { setSidebarOpen(false); loadSAFEHistory(); setShowConversationHistory(true); }}
                  style={{ padding: '8px 12px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textPrimary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📜</span> Conversation History
                </button>

                <div style={{ height: '1px', background: SAFE_THEME.border, margin: '4px 0' }} />

                {/* ── Change Home App ── */}
                {!getPilotTheme() && (<>
                <button onClick={() => navigate('/select-home-app')}
                  style={{ padding: '8px 12px', background: 'rgba(79,70,229,0.15)', border: '1px solid rgba(79,70,229,0.4)', borderRadius: '8px', color: '#a5b4fc', textAlign: 'left', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🏠</span> Change Home App
                </button>

                <button onClick={() => navigate('/emit-the-bible')}
                  style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textSecondary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✨</span> Emit / 💫 SAGE
                </button>

                <button onClick={() => navigate('/family-coach')}
                  style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textSecondary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>👨‍👩‍👧</span> Family Coach
                </button>

                <button onClick={() => navigate('/mental-performance')}
                  style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${SAFE_THEME.border}`, borderRadius: '8px', color: SAFE_THEME.textSecondary, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🏆</span> Mental Performance
                </button>
                </>)}

                {!getPilotTheme() && (
                <button onClick={() => navigate('/journey')}
                  style={{ padding: '8px 12px', background: `linear-gradient(135deg, ${SAFE_THEME.primary}30, ${SAFE_THEME.accent}30)`, border: `1px solid ${SAFE_THEME.accent}50`, borderRadius: '8px', color: SAFE_THEME.accent, textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✨</span> My Journey
                </button>
                )}

                <button onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}
                  style={{ padding: '8px 12px', background: 'rgba(220,53,69,0.15)', border: '1px solid rgba(220,53,69,0.3)', borderRadius: '8px', color: '#ff6b6b', textAlign: 'left', cursor: 'pointer', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span>🚪</span> Logout
                </button>
                <DeleteAccountButton variant="dark" />
                <div style={{ color: SAFE_THEME.textSecondary, fontSize: '13px', lineHeight: 1.4, textAlign: 'center', padding: '10px 4px 2px', letterSpacing: '0.02em' }}>
                  Built by MindStreams Technology
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px' }}>
          {messages.length === 0 ? (
            <PilotWelcome theme={safeContext()} name={profile.preferred_name} palette={SAFE_THEME} onStart={(text) => sendMessage(text)} />
          ) : (
            messages.map((msg, i) => (
              <div key={i} style={{ marginBottom: '16px', display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '80%', padding: '14px 18px', borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px', background: msg.role === 'user' ? SAFE_THEME.userBubble : SAFE_THEME.assistantBubble, color: SAFE_THEME.textPrimary, whiteSpace: 'pre-wrap', lineHeight: '1.6', boxShadow: msg.role === 'user' ? `0 2px 8px ${SAFE_THEME.glow}` : '0 2px 8px rgba(0,0,0,0.2)' }}>
                  {renderMessageContent(msg.content)}
                  {msg.isStreaming && <span style={{ display: 'inline-block', width: '8px', height: '8px', background: SAFE_THEME.primary, borderRadius: '50%', marginLeft: '8px', animation: 'pulse 1s infinite' }} />}
                  {msg.role === 'assistant' && !msg.isStreaming && <FeedbackButton sessionId={conversationId || sessionId} messageIndex={i} context={safeContext().id} replyText={msg.content} />}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{ padding: '12px 16px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: `1px solid ${SAFE_THEME.border}`, background: SAFE_THEME.pageBg }}>
          <div style={{ marginBottom: '12px' }}>
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress}
              placeholder="Share what's on your mind..." rows={1}
              style={{ width: '100%', padding: '14px 16px', background: SAFE_THEME.inputBg, border: `1px solid ${SAFE_THEME.border}`, borderRadius: '16px', color: SAFE_THEME.textPrimary, fontSize: '16px', resize: 'none', outline: 'none', minHeight: '48px', maxHeight: '120px', boxSizing: 'border-box', transition: 'border-color 0.2s ease' }}
              onFocus={(e) => e.target.style.borderColor = SAFE_THEME.primary}
              onBlur={(e) => e.target.style.borderColor = SAFE_THEME.border} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => { if (voice.isListening) { voice.stopListening(); if (voice.transcript && voice.transcript.trim()) setTimeout(() => sendMessage(), 100); } else { voice.startListening(); } }} disabled={isLoading}
              style={{ padding: '12px', background: voice.isListening ? SAFE_THEME.listening : SAFE_THEME.primaryGradient, border: 'none', borderRadius: '50%', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '20px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isLoading ? 0.5 : 1, boxShadow: voice.isListening ? `0 0 0 3px ${SAFE_THEME.accent}50` : `0 2px 8px ${SAFE_THEME.primary}40`, transition: 'all 0.3s' }}
              title={voice.isListening ? 'Click to stop and send' : 'Click to speak'}>
              {voice.isListening ? '🔴' : '🎤'}
            </button>
            <button onClick={() => voice.toggleVoice()}
              style={{ padding: '12px', background: voice.voiceEnabled ? SAFE_THEME.primaryGradient : SAFE_THEME.inputBg, border: voice.voiceEnabled ? 'none' : `1px solid ${SAFE_THEME.border}`, borderRadius: '50%', cursor: 'pointer', fontSize: '20px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' }}
              title={voice.voiceEnabled ? 'Voice responses ON' : 'Voice responses OFF'}>
              {voice.voiceEnabled ? '🔊' : '🔇'}
            </button>
            <button onClick={() => sendMessage()} disabled={isLoading || !input.trim()}
              style={{ padding: '12px 24px', background: isLoading || !input.trim() ? SAFE_THEME.glow : SAFE_THEME.primaryGradient, border: 'none', borderRadius: '24px', color: '#fff', cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '600', opacity: isLoading || !input.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s', boxShadow: isLoading || !input.trim() ? 'none' : `0 4px 15px ${SAFE_THEME.glowStrong}` }}>
              {isLoading ? '...' : 'Send →'}
            </button>
          </div>
        </div>

        {/* Topics Modal */}
        {showTopicsModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ background: SAFE_THEME.cardBg, borderRadius: '20px', maxWidth: '900px', width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${SAFE_THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: SAFE_THEME.sidebarBg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>🌿</span>
                  <div>
                    <div style={{ color: SAFE_THEME.textPrimary, fontSize: '20px', fontWeight: '700' }}>Explore 30 Mental Health Topics</div>
                    <div style={{ color: SAFE_THEME.textSecondary, fontSize: '14px' }}>Select a topic to explore with SAFE</div>
                  </div>
                </div>
                <button onClick={() => setShowTopicsModal(false)} style={{ background: 'none', border: 'none', color: SAFE_THEME.textMuted, fontSize: '28px', cursor: 'pointer', padding: '4px', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                  {mentalHealthTopics.map((topic, idx) => (
                    <button key={idx}
                      onClick={() => { setSidebarOpen(false); setShowTopicsModal(false); sendMessage(`I'd like help with: ${topic}`); }}
                      style={{ padding: '14px 18px', background: idx < 20 ? SAFE_THEME.primaryGradient : `linear-gradient(135deg, ${SAFE_THEME.secondary}, ${SAFE_THEME.accent})`, border: 'none', borderRadius: '10px', color: idx < 20 ? SAFE_THEME.textPrimary : SAFE_THEME.onPrimary, fontSize: '14px', fontWeight: '500', cursor: 'pointer', textAlign: 'left', transition: 'transform 0.2s, box-shadow 0.2s' }}
                      onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = `0 4px 12px ${SAFE_THEME.primary}40`; }}
                      onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}>
                      {topic}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.95); } }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          html, body { background: ${SAFE_THEME.pageBg} !important; }
          ::placeholder { color: ${SAFE_THEME.textMuted}; }
        `}</style>
      </div>

      {/* Conversation History Modal */}
      {showConversationHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: SAFE_THEME.cardBg, borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: `1px solid ${SAFE_THEME.border}` }}>
            <div style={{ padding: '20px', borderBottom: `1px solid ${SAFE_THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: SAFE_THEME.textPrimary }}>📜 Conversation History</h2>
              <button onClick={() => setShowConversationHistory(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: SAFE_THEME.textMuted }}>×</button>
            </div>
            <div style={{ padding: '16px', maxHeight: '50vh', overflowY: 'auto' }}>
              {loadingHistory ? <p style={{ color: SAFE_THEME.textMuted, textAlign: 'center' }}>Loading conversations...</p>
              : safeConversations.length === 0 ? <p style={{ color: SAFE_THEME.textMuted, textAlign: 'center' }}>No conversations yet</p>
              : safeConversations.map((conv, idx) => (
                <div key={conv.id || idx} onClick={() => loadConversationMessages(conv.id)}
                  style={{ padding: '16px', marginBottom: '12px', borderRadius: '12px', background: conversationId === conv.id ? 'rgba(59,130,246,0.2)' : SAFE_THEME.inputBg, cursor: 'pointer', border: conversationId === conv.id ? '2px solid #3b82f6' : '2px solid transparent', transition: 'all 0.2s ease' }}>
                  <div style={{ fontWeight: '600', color: SAFE_THEME.textPrimary, marginBottom: '4px' }}>{conv.preview ? conv.preview.substring(0, 60) + '...' : 'Conversation'}</div>
                  <div style={{ fontSize: '13px', color: SAFE_THEME.textMuted }}>{conv.message_count || 0} messages{conv.last_activity && ` • ${new Date(conv.last_activity).toLocaleDateString()}`}</div>
                  <ConversationActions app="safe" color={SAFE_THEME.textMuted} conversationId={conv.id} onDeleted={(id) => { setSafeConversations(prev => prev.filter(c => c.id !== id)); if (conversationId === id) { setConversationId(null); setMessages([]); } }} />
                </div>
              ))}
            </div>
            <div style={{ padding: '16px', borderTop: `1px solid ${SAFE_THEME.border}`, textAlign: 'center' }}>
              <button onClick={() => { setConversationId(null); setMessages([]); setShowConversationHistory(false); }}
                style={{ padding: '12px 24px', background: SAFE_THEME.accent, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                + Start New Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </>
  );
};

export default SAFEChat;
